import { MilestonePaymentStatusEnum } from "../modules/shared/model";

/**
 * Check if all milestones in a contract have been released
 * @param milestones Array of milestones with payment status
 * @returns boolean indicating if all milestones are released
 */
export const areAllMilestonesReleased = (milestones: any[]): boolean => {
    if (!milestones || milestones.length === 0) {
        return false;
    }

    return milestones.every(milestone => {
        // Check if milestone has escrow and if escrow status is released
        if (milestone.escrow) {
            const statusName = milestone.escrow.status?.name?.toLowerCase() || milestone.escrow.status?.toString().toLowerCase() || "";
            return statusName === "released";
        }

        // Fallback: check payment status
        const paymentStatus = milestone.paymentStatus?.toLowerCase() || "";
        return paymentStatus === "released";
    });
};

/**
 * Check if a specific milestone is released
 * @param milestone Milestone object with payment status
 * @returns boolean indicating if milestone is released
 */
export const isMilestoneReleased = (milestone: any): boolean => {
    console.log("milestone11", milestone);
    if (!milestone) {
        return false;
    }

    // Check if milestone has escrow and if escrow status is released
    if (milestone.escrow) {
        const statusName = milestone.escrow.status?.name?.toLowerCase() || milestone.escrow.status?.toString().toLowerCase() || "";
        return statusName === "released";
    }

    // Fallback: check payment status
    const paymentStatus = milestone.paymentStatus?.toLowerCase() || "";
    return paymentStatus === "released";
};

/**
 * Get the latest payment status for a milestone
 * @param milestoneId ID of the milestone
 * @param milestonePaymentLogsMap Map of milestone payment logs
 * @returns Latest payment status or null
 */
export const getLatestMilestonePaymentStatus = (milestoneId: string, milestonePaymentLogsMap: { [milestoneId: string]: any[] }): any => {
    const logs = milestonePaymentLogsMap[milestoneId] || [];
    if (logs.length === 0) return null;

    // Create a copy of the array before sorting to avoid mutating the original
    const logsCopy = [...logs];
    const sortedLogs = logsCopy.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return sortedLogs[0];
};

/**
 * Check if milestone payment status is released based on payment logs
 * @param milestoneId ID of the milestone
 * @param milestonePaymentLogsMap Map of milestone payment logs
 * @returns boolean indicating if milestone payment is released
 */
export const isMilestonePaymentReleased = (milestoneId: string, milestonePaymentLogsMap: { [milestoneId: string]: any[] }): boolean => {
    const latestLog = getLatestMilestonePaymentStatus(milestoneId, milestonePaymentLogsMap);
    if (!latestLog) return false;

    return Number(latestLog.paymentStatus?.id) === MilestonePaymentStatusEnum.Released;
};

/**
 * Check if all milestones are released based on payment logs
 * @param milestones Array of milestones
 * @param milestonePaymentLogsMap Map of milestone payment logs
 * @returns boolean indicating if all milestones are released
 */
export const areAllMilestonesReleasedByPaymentLogs = (milestones: any[], milestonePaymentLogsMap: { [milestoneId: string]: any[] }): boolean => {
    if (!milestones || milestones.length === 0) {
        return false;
    }

    return milestones.every(milestone =>
        isMilestonePaymentReleased(milestone.id, milestonePaymentLogsMap)
    );
};

/**
 * Check if the current user has already reviewed a milestone
 * @param milestone - The milestone object that may contain a reviews array
 * @param currentUserId - The ID of the current user
 * @returns boolean - true if user has already reviewed, false otherwise
 */
export const hasUserReviewedMilestone = (milestone: any, currentUserId: string): boolean => {
    // Check if milestone has reviews array
    if (!milestone.reviews || !Array.isArray(milestone.reviews)) {
        return false;
    }
    
    // Check if any review in the array has the current user as the author
    return milestone.reviews.some((review: any) => 
        review.author?.id === currentUserId
    );
};
